import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IdeozLoading, NotFoundState } from '@ideoz/ui';
import { useScopeConversations, useStartups } from '@ideoz/core';

/**
 * Scope entry resolver — `/:startupSlug/:scopeId`.
 *
 * Decides which conversation to open *before* the chat workspace mounts, then
 * redirects (replace). This removes the old empty-`?chat=` limbo where the
 * workspace rendered in an indeterminate state and multiple effects raced to
 * correct it (spurious greeting / blank page).
 *
 *   - Scope has conversations → open the most-recent one (`/:slug/:scopeId/:id`).
 *   - Brand-new scope → the explicit new-chat route (`/:slug/:scopeId/new`),
 *     where exactly one `auto_prompt` greeting fires.
 */
export default function ScopeConversationResolver(): JSX.Element {
  const { startupSlug, scopeId } = useParams<{ startupSlug: string; scopeId: string }>();
  const navigate = useNavigate();

  const {
    data: startups = [],
    isFetched: startupsFetched,
    isFetching: startupsFetching,
    isError: startupsError,
  } = useStartups();
  const matchedStartup = startups.find((s) => s.slug === startupSlug) ?? null;
  const startupId = matchedStartup?.id ?? null;

  // Shared cache (keyed by scope) — reused by the workspace + sub-pages, so this
  // resolve doesn't cost an extra fetch. `isSuccess` distinguishes "not loaded"
  // from "loaded and genuinely empty".
  const { data: convData, isSuccess: conversationsLoaded } = useScopeConversations(
    startupId,
    scopeId
  );

  useEffect(() => {
    if (!startupSlug || !scopeId || !conversationsLoaded) return;
    const convs = convData ?? [];
    // updatedAt-DESC → [0] is the most recent chat; empty → explicit new-chat.
    const target = convs.length > 0 ? convs[0].id : 'new';
    navigate(`/${startupSlug}/${scopeId}/${target}`, { replace: true });
  }, [startupSlug, scopeId, conversationsLoaded, convData, navigate]);

  // 404 only once the startups query has settled (same guard as the workspace).
  if (startupsFetched && !startupsFetching && !startupsError && (!matchedStartup || !startupId)) {
    return (
      <NotFoundState
        description="Startup not found"
        actionLabel="Back to My Startups"
        onAction={() => navigate('/startups')}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <IdeozLoading size="lg" />
    </div>
  );
}
